import { useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import {
  REVIEW_STATUS_LABELS,
  bulkResultMessage,
  type AnswerShortlistRequest,
  type RankingView,
  type ReviewMoveResult,
  type ReviewPeopleView,
  type ReviewStatus,
  type ShortlistCandidate,
  type ShortlistView,
} from '@nest-recruit/shared';
import { apiGet, apiPost, apiPut, errorMessage, type ApiError } from '../../../lib/api-client';
import { useCurrentSession } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { startBulkAction, undoAction } from '../../pipeline/api/pipeline-api';
import { rankingKey } from '../../ranking/api/ranking-api';
import { HM_ACTIONS_KEY } from '../../requisitions/api/requisitions-api';
import { mayGoOn, maySendTestFromShortlist } from '../proceed-rule';

// R6 — HR review and the shortlist hand-off (§L31). SHL-01 and SHL-02 sit under `['shortlists']`,
// the key the API's hints name for the job room and for each manager; the conveyor is drawn from
// RNK-01's own data (`['applications', 'ranking', jobId]`), so the lanes and the ranking agree.

export type ShortlistScreen = 'manager' | 'hr';

export function shortlistKey(jobId: string, screen: ShortlistScreen): readonly unknown[] {
  return ['shortlists', jobId, screen];
}

/** SHL-01 for the manager, SHL-02 for HR. */
export function useShortlist(jobId: string, screen: ShortlistScreen): UseQueryResult<ShortlistView, ApiError> {
  return useQuery<ShortlistView, ApiError>({
    queryKey: shortlistKey(jobId, screen),
    queryFn: () => apiGet<ShortlistView>(screen === 'manager' ? `/shortlists/${jobId}` : `/jobs/${jobId}/shortlist`),
    placeholderData: (previous) => previous,
  });
}

/** D6 (ADR 0080): under `['applications']`, so every hint that redraws the lanes redraws who reviewed them too. */
export function reviewPeopleKey(jobId: string): readonly unknown[] {
  return ['applications', 'review-people', jobId];
}

/** Who made each review on the job, and each person's CV, for the Review list. */
export function useReviewPeople(jobId: string): UseQueryResult<ReviewPeopleView, ApiError> {
  return useQuery<ReviewPeopleView, ApiError>({
    queryKey: reviewPeopleKey(jobId),
    queryFn: () => apiGet<ReviewPeopleView>(`/jobs/${jobId}/review/people`),
    placeholderData: (previous) => previous,
  });
}

/** SHL-01 "Open CV": the shortlist's own link, so a line manager who can't open CVs elsewhere can here. */
export function shortlistCvUrl(jobId: string, applicationId: string): string {
  return `/api/v1/shortlists/${jobId}/cv/${applicationId}`;
}

type Patch = Partial<ShortlistCandidate>;

/** Draws a change on both screens' cached copies; returns the way back. */
function draw(queryClient: QueryClient, jobId: string, ids: ReadonlySet<string>, patch: (candidate: ShortlistCandidate) => Patch | null): () => void {
  const snapshot = queryClient.getQueriesData<ShortlistView>({ queryKey: ['shortlists', jobId] });
  queryClient.setQueriesData<ShortlistView>({ queryKey: ['shortlists', jobId] }, (view) =>
    view
      ? {
          ...view,
          candidates: view.candidates.flatMap((candidate) => {
            if (!ids.has(candidate.applicationId)) return [candidate];
            const change = patch(candidate);
            return change === null ? [] : [{ ...candidate, ...change }];
          }),
        }
      : view,
  );
  return () => {
    for (const [key, view] of snapshot) queryClient.setQueryData(key, view);
  };
}

/** The server's copy has landed — only now does the optimistic state let go (§5a rule 3). */
async function settle(queryClient: QueryClient, jobId: string): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['shortlists', jobId] }),
    queryClient.invalidateQueries({ queryKey: ['applications'] }),
    queryClient.invalidateQueries({ queryKey: HM_ACTIONS_KEY }),
  ]);
}

/** The pieces of a hook that marks the rows it is saving, and only those (§5a rule 4). */
function usePending(): readonly [ReadonlySet<string>, (ids: readonly string[], isOn: boolean) => void] {
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const mark = useCallback((ids: readonly string[], isOn: boolean) => {
    setPending((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (isOn) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);
  return [pending, mark];
}

export type AnswerChoice = Omit<AnswerShortlistRequest, 'version'>;

/** F24.02: the answer shows at once; a refusal ("Sarah already answered") puts it back and says why. */
export function useAnswerShortlist(jobId: string): { readonly answer: (candidate: ShortlistCandidate, choice: AnswerChoice) => Promise<boolean>; readonly pending: ReadonlySet<string> } {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const me = useCurrentSession()?.fullName ?? null;
  const [pending, mark] = usePending();
  const answer = useCallback(
    async (candidate: ShortlistCandidate, choice: AnswerChoice): Promise<boolean> => {
      const id = candidate.applicationId;
      const restore = draw(queryClient, jobId, new Set([id]), () => ({
        answer: choice.answer,
        reason: choice.answer === 'no' ? (choice.reason ?? null) : null,
        reasonCode: choice.answer === 'no' ? (choice.reasonCode ?? null) : null,
        comment: choice.comment ?? null,
        answeredByName: me,
        answeredAt: new Date().toISOString(),
      }));
      mark([id], true);
      try {
        await apiPost(`/applications/${id}/shortlist-answer`, { ...choice, version: candidate.version });
        await settle(queryClient, jobId);
        return true;
      } catch (error) {
        restore();
        show({ tone: 'danger', message: `${candidate.candidate.fullName}: answer not saved`, detail: errorMessage(error) });
        return false;
      } finally {
        mark([id], false);
      }
    },
    [queryClient, jobId, me, mark, show],
  );
  return { answer, pending };
}

/**
 * F24.03, Done. It shows as Done at once — every sent person locked, as the server will have them
 * (§5a rule 1) — and stays so until the server's view has landed (rule 3); a refusal puts the
 * screen back as it was and says why.
 */
export function useFinishShortlist(jobId: string): { readonly finish: () => Promise<boolean>; readonly isFinishing: boolean } {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const [isFinishing, setIsFinishing] = useState(false);
  const finish = useCallback(async (): Promise<boolean> => {
    const key = shortlistKey(jobId, 'manager');
    // A refetch already on its way would land the old "not done" over the drawn Done.
    await queryClient.cancelQueries({ queryKey: key });
    const before = queryClient.getQueryData<ShortlistView>(key);
    if (before) {
      queryClient.setQueryData<ShortlistView>(key, {
        ...before,
        isDone: true,
        candidates: before.candidates.map((candidate) => (candidate.shortlistId === null ? candidate : { ...candidate, isLocked: true })),
      });
    }
    setIsFinishing(true);
    try {
      const view = await apiPost<ShortlistView>(`/shortlists/${jobId}/done`);
      queryClient.setQueryData(key, view);
      await settle(queryClient, jobId);
      show({ tone: 'success', message: 'Done — HR has been told your answers' });
      return true;
    } catch (error) {
      if (before) queryClient.setQueryData(key, before);
      show({ tone: 'danger', message: 'Not marked done', detail: errorMessage(error) });
      return false;
    } finally {
      setIsFinishing(false);
    }
  }, [queryClient, jobId, show]);
  return { finish, isFinishing };
}

/** F24.01. The ones being sent show as Waiting at once; the server's view replaces them. */
export function useSendShortlist(jobId: string): { readonly send: () => Promise<boolean>; readonly isSending: boolean } {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const [isSending, setIsSending] = useState(false);
  const send = useCallback(async (): Promise<boolean> => {
    const view = queryClient.getQueryData<ShortlistView>(shortlistKey(jobId, 'hr'));
    const ids = new Set(view?.candidates.filter((candidate) => candidate.answer === 'not_sent').map((candidate) => candidate.applicationId) ?? []);
    const now = new Date().toISOString();
    const restore = draw(queryClient, jobId, ids, () => ({ answer: 'awaiting', sentAt: now }));
    setIsSending(true);
    try {
      const sent = await apiPost<ShortlistView>(`/jobs/${jobId}/shortlist/send`);
      queryClient.setQueryData(shortlistKey(jobId, 'hr'), sent);
      await settle(queryClient, jobId);
      const names = sent.recipients.map((recipient) => recipient.fullName).join(' and ');
      show({ tone: 'success', message: `Sent ${ids.size} to ${names}`, detail: 'They get one email and a notification each.' });
      return true;
    } catch (error) {
      restore();
      show({ tone: 'danger', message: 'The shortlist was not sent', detail: errorMessage(error) });
      return false;
    } finally {
      setIsSending(false);
    }
  }, [queryClient, jobId, show]);
  return { send, isSending };
}

/** F24.05 "Move to contact": HR goes ahead without the manager (O30). With phone Screening on (ADR 0079) they land there instead. */
export function useProceedShortlist(jobId: string, destination = 'Contact', isManagerStepOn = true): { readonly proceed: (candidates: readonly ShortlistCandidate[]) => Promise<void>; readonly pending: ReadonlySet<string> } {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const [pending, mark] = usePending();
  const proceed = useCallback(
    async (candidates: readonly ShortlistCandidate[]): Promise<void> => {
      const waiting = candidates.filter((candidate) => mayGoOn(candidate, isManagerStepOn));
      if (waiting.length === 0) {
        show({ tone: 'info', message: isManagerStepOn ? 'Nobody selected is waiting for the manager' : 'Everyone selected has moved on already' });
        return;
      }
      const ids = waiting.map((candidate) => candidate.applicationId);
      const restore = draw(queryClient, jobId, new Set(ids), () => ({ answer: 'hr_proceeded' }));
      mark(ids, true);
      try {
        const result = await apiPost<ReviewMoveResult>(`/jobs/${jobId}/shortlist/proceed`, { applicationIds: ids });
        await settle(queryClient, jobId);
        show({ tone: 'success', message: `${result.moved.length} moved to ${destination}`, detail: result.skipped[0]?.message });
      } catch (error) {
        restore();
        show({ tone: 'danger', message: 'Nobody was moved', detail: errorMessage(error) });
      } finally {
        mark(ids, false);
      }
    },
    [queryClient, jobId, destination, isManagerStepOn, mark, show],
  );
  return { proceed, pending };
}

/**
 * ADR 0047 — "Send test" from ⑤ Shortlist: the pipeline's own "Assign assessment", so the move
 * and the link follow one path. The rows leave the shortlist at once and stay gone unless the
 * server refuses them (§5a rules 1 and 3); a test taken outside the app sends nothing.
 */
export function useSendTestFromShortlist(jobId: string, isByHand: boolean): { readonly sendTest: (candidates: readonly ShortlistCandidate[]) => Promise<void> } {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const sendTest = useCallback(
    async (candidates: readonly ShortlistCandidate[]): Promise<void> => {
      const ready = candidates.filter(maySendTestFromShortlist);
      if (ready.length === 0) {
        show({ tone: 'info', message: 'Nobody selected can be sent the test', detail: 'Only people not sent yet, or waiting for the manager, can skip ahead.' });
        return;
      }
      const ids = ready.map((candidate) => candidate.applicationId);
      const restore = draw(queryClient, jobId, new Set(ids), () => null);
      try {
        const view = await startBulkAction(jobId, { action: 'assign_assessment', selection: { kind: 'ids', ids } });
        await Promise.all([settle(queryClient, jobId), queryClient.invalidateQueries({ queryKey: ['assessment-results'] })]);
        const people = `${ready.length} ${ready.length === 1 ? 'person' : 'people'}`;
        if (view.failedCount > 0) {
          show({ tone: 'warning', message: bulkResultMessage(view), detail: view.failures.slice(0, 2).map((failure) => `${failure.candidateName}: ${failure.message}`).join(' ') });
          return;
        }
        show(
          isByHand
            ? { tone: 'success', message: `Moving ${people} to Assessment`, detail: 'Record each result on the Assessment tab once they have taken the test.' }
            : { tone: 'success', message: `Sending the test to ${people}`, detail: 'They move to Assessment, and each gets a personal link with a deadline.' },
        );
      } catch (error) {
        restore();
        show({ tone: 'danger', message: 'The test wasn’t sent', detail: errorMessage(error) });
      }
    },
    [queryClient, jobId, isByHand, show],
  );
  return { sendTest };
}

export interface RejectPools {
  readonly poolIds: readonly string[];
  readonly newPoolNames: readonly string[];
  /** ADR 0046: the reject box's "Also add to CV Bank", carried with the rejection. */
  readonly addToCvBank?: boolean;
}

const NO_POOLS: RejectPools = { poolIds: [], newPoolNames: [] };

/**
 * "Reject the No's" (§L31) and a reject from the conveyor or RNK-01: the pipeline's own reject
 * (S16), so it holds the regret email until the job closes and offers §L9's undo.
 */
export function useRejectWithReason(jobId: string): {
  readonly reject: (people: readonly { readonly applicationId: string; readonly name: string }[], reasonCode: string, pools?: RejectPools) => Promise<boolean>;
  readonly pending: ReadonlySet<string>;
} {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const showUndo = useToasts((toasts) => toasts.showUndo);
  const [pending, mark] = usePending();
  const reject = useCallback(
    async (people: readonly { readonly applicationId: string; readonly name: string }[], reasonCode: string, pools: RejectPools = NO_POOLS): Promise<boolean> => {
      const ids = people.map((person) => person.applicationId);
      const restoreShortlist = draw(queryClient, jobId, new Set(ids), () => null);
      const restoreRanking = drawRanking(queryClient, jobId, new Set(ids), () => ({ stage: 'rejected' }));
      mark(ids, true);
      try {
        const view = await startBulkAction(jobId, { action: 'reject', selection: { kind: 'ids', ids }, reasonCode, poolIds: [...pools.poolIds], newPoolNames: [...pools.newPoolNames], addToCvBank: pools.addToCvBank ?? false });
        await settle(queryClient, jobId);
        const message = people.length === 1 && people[0] ? `${people[0].name} rejected` : bulkResultMessage(view);
        if (view.canUndo) {
          showUndo(message, () => {
            void undoAction(view.id)
              .then(() => settle(queryClient, jobId))
              .catch((error: unknown) => show({ tone: 'danger', message: "Couldn't undo that", detail: errorMessage(error) }));
          });
        } else {
          show({ tone: view.failedCount > 0 ? 'warning' : 'success', message });
        }
        return true;
      } catch (error) {
        restoreShortlist();
        restoreRanking();
        show({ tone: 'danger', message: 'Nobody was rejected', detail: errorMessage(error) });
        return false;
      } finally {
        mark(ids, false);
      }
    },
    [queryClient, jobId, mark, show, showUndo],
  );
  return { reject, pending };
}

/**
 * F23.18 (ADR 0057): a rejected person brought back into a review lane while the job is open. The
 * card shows "Saving…" in Rejected until the lanes refetch with them in their new lane.
 */
export function useBringBack(jobId: string): {
  readonly bringBack: (person: { readonly applicationId: string; readonly name: string }, to: ReviewStatus) => Promise<boolean>;
  readonly pending: ReadonlySet<string>;
} {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const [pending, mark] = usePending();
  const bringBack = useCallback(
    async (person: { readonly applicationId: string; readonly name: string }, to: ReviewStatus): Promise<boolean> => {
      mark([person.applicationId], true);
      try {
        await apiPost(`/jobs/${jobId}/review/bring-back`, { applicationId: person.applicationId, to });
        await settle(queryClient, jobId);
        show({ tone: 'success', message: `${person.name} is back in ${REVIEW_STATUS_LABELS[to]}` });
        return true;
      } catch (error) {
        show({ tone: 'danger', message: `Couldn't bring ${person.name} back`, detail: errorMessage(error) });
        return false;
      } finally {
        mark([person.applicationId], false);
      }
    },
    [queryClient, jobId, mark, show],
  );
  return { bringBack, pending };
}

// --- Review (F23.06–F23.08) -----------------------------------------------------------------

type RankingRowPatch = Partial<RankingView['rows'][number]>;

/**
 * The Review list's "Reviewed by" and its Mine filter follow a move at once: the mover is the reviewer of a Shortlisted or Maybe, and
 * nobody is of a To review (ADR 0080). Returns the way back.
 */
function drawReviewer(queryClient: QueryClient, jobId: string, ids: ReadonlySet<string>, target: ReviewStatus, me: { readonly userId: string; readonly name: string } | null): () => void {
  const key = reviewPeopleKey(jobId);
  const before = queryClient.getQueryData<ReviewPeopleView>(key);
  const reviewedBy = target === 'to_review' || me === null ? null : { userId: me.userId, name: me.name, at: new Date().toISOString() };
  if (before) queryClient.setQueryData<ReviewPeopleView>(key, { ...before, people: before.people.map((person) => (ids.has(person.applicationId) ? { ...person, reviewedBy } : person)) });
  return () => {
    if (before) queryClient.setQueryData(key, before);
  };
}

function drawRanking(queryClient: QueryClient, jobId: string, ids: ReadonlySet<string>, patch: (row: RankingView['rows'][number]) => RankingRowPatch): () => void {
  const key = rankingKey(jobId);
  const before = queryClient.getQueryData<RankingView>(key);
  if (before) queryClient.setQueryData<RankingView>(key, { ...before, rows: before.rows.map((row) => (ids.has(row.applicationId) ? { ...row, ...patch(row) } : row)) });
  return () => {
    if (before) queryClient.setQueryData(key, before);
  };
}

export interface ReviewMoveItem {
  readonly applicationId: string;
  readonly name: string;
  readonly from: ReviewStatus;
}

/**
 * The conveyor's lanes and RNK-01's "select → Shortlist" (F23.07): the cards move at once, the
 * server confirms, and Undo (F23.08) for 10 s puts each back where it was.
 */
export function useReviewMove(jobId: string): { readonly move: (items: readonly ReviewMoveItem[], to: ReviewStatus) => Promise<void>; readonly pending: ReadonlySet<string> } {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const showUndo = useToasts((toasts) => toasts.showUndo);
  const session = useCurrentSession();
  const myId = session?.id ?? null;
  const myName = session?.fullName ?? '';
  const [pending, mark] = usePending();

  const move = useCallback(
    (items: readonly ReviewMoveItem[], to: ReviewStatus): Promise<void> => {
      // Named, so its own Undo can run it again (back to each card's lane).
      async function run(batch: readonly ReviewMoveItem[], target: ReviewStatus, isUndo: boolean): Promise<void> {
        const moving = batch.filter((item) => item.from !== target);
        if (moving.length === 0) return;
        const ids = moving.map((item) => item.applicationId);
        const restore = drawRanking(queryClient, jobId, new Set(ids), () => ({ reviewStatus: target, managerResponse: target === 'shortlisted' ? 'not_sent' : null }));
        const restoreReviewer = drawReviewer(queryClient, jobId, new Set(ids), target, myId === null ? null : { userId: myId, name: myName });
        mark(ids, true);
        try {
          const result = await apiPost<ReviewMoveResult>(`/jobs/${jobId}/review/move`, { applicationIds: ids, to: target });
          await settle(queryClient, jobId);
          const skipped = result.skipped;
          if (skipped.length > 0) show({ tone: 'warning', message: `${skipped.length} stayed where they were`, detail: skipped[0]?.message });
          if (isUndo || result.moved.length === 0) return;
          const moved = moving.filter((item) => result.moved.includes(item.applicationId));
          const message = moved.length === 1 && moved[0] ? `${moved[0].name} moved to ${REVIEW_STATUS_LABELS[target]}` : `${moved.length} moved to ${REVIEW_STATUS_LABELS[target]}`;
          showUndo(message, () => {
            for (const from of new Set(moved.map((item) => item.from))) {
              void run(
                moved.filter((item) => item.from === from).map((item) => ({ ...item, from: target })),
                from,
                true,
              );
            }
          });
        } catch (error) {
          restore();
          restoreReviewer();
          show({ tone: 'danger', message: 'Nobody moved', detail: errorMessage(error) });
        } finally {
          mark(ids, false);
        }
      }
      return run(items, to, false);
    },
    [queryClient, jobId, mark, show, showUndo, myId, myName],
  );
  return { move, pending };
}

export interface RatingResult {
  readonly ratings: Readonly<Record<string, number>>;
  readonly hrStars: number | null;
}

/** F23.06: one line's rating. The card draws it at once; this saves it. */
export function saveRating(applicationId: string, lineKey: string, score: number | null): Promise<RatingResult> {
  return apiPut<RatingResult>(`/applications/${applicationId}/review-ratings/${lineKey}`, { score });
}
